// src/encounter/intelligence/use-intel-warm.ts
//
// The Today screen's hook for warm-from-Today (intel-warm.ts): picks the clinician's NEXT visit (the first one in
// progress, else the first upcoming) and warms its Explain once, behind the `intelWarm` switch and the engine being on.
import { useEffect } from 'react';
import { env } from '@/config/env';
import { authStorage } from '@/auth/storage';
import { get, ApiError } from '@/shared/api/api-client';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import type { ListAppointmentItem } from '@/types/appointment-api';
import type { EncounterModel } from '@/types/encounter-api';
import { warmNextVisit, type WarmTarget } from './intel-warm';

/** The next visit OF THIS CLINICIAN: the first in progress, else the first upcoming, with a patient on it, whose provider
 *  is the signed-in practitioner (Codex: a nurse's Today can list other providers' visits; those are never warmed). */
export function pickNextVisit(b: { inProgress: ListAppointmentItem[]; upcoming: ListAppointmentItem[] }, practitionerId: string | null | undefined): WarmTarget | null {
  if (!practitionerId) return null;
  const mine = (item: ListAppointmentItem) => item.participant?.some((p) => (p.provider?.id ?? '').toLowerCase() === practitionerId.toLowerCase());
  for (const item of [...b.inProgress, ...b.upcoming]) {
    const patientId = item.participant?.find((p) => p.patient)?.patient?.id;
    if (patientId && mine(item)) return { appointmentId: item.id, patientId };
  }
  return null;
}

/** The visit's encounter id, read the way the visit screen reads it (GET by appointment; 404 = none yet). */
async function encounterOf(appointmentId: string): Promise<string | null> {
  try {
    const e = await get<EncounterModel | null>(`/api/Patients/GetEncounterByEncounterId?AppointmentId=${encodeURIComponent(appointmentId)}`);
    return e?.identifier ?? null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** When the warm starts (rail session, 4 Nov: no fixed timer, a hidden or throttled tab must not skip it). A visible page:
 *  on its first idle moment, at the latest after IDLE_CAP_MS. A hidden page: at once (a hidden tab's timers and idle
 *  callbacks can be held back for minutes). Returns the cancel for the effect's cleanup. */
export const IDLE_CAP_MS = 1000;
export function scheduleWarm(start: () => void, w: { document?: Pick<Document, 'visibilityState'>; requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void } = globalThis as never): () => void {
  if (w.document?.visibilityState === 'hidden' || typeof w.requestIdleCallback !== 'function') {
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) start(); });
    return () => { cancelled = true; };
  }
  const id = w.requestIdleCallback(start, { timeout: IDLE_CAP_MS });
  return () => w.cancelIdleCallback?.(id);
}

/** `b` null until the list AND the in-progress lookup are both settled (Codex: else a first pick could be replaced). */
const ENCOUNTER_LOOKUP_MS = 8000;
/** The encounter lookup, bounded: a stalled read fails the warm (the visit then runs its own Explain as before). */
const boundedEncounterOf = (appointmentId: string) =>
  Promise.race([encounterOf(appointmentId), new Promise<never>((_, reject) => setTimeout(() => reject(new Error('encounter lookup timed out')), ENCOUNTER_LOOKUP_MS))]);

export function useIntelWarm(b: { inProgress: ListAppointmentItem[]; upcoming: ListAppointmentItem[] } | null, practitionerId: string | null | undefined) {
  useIntelWarmTarget(b ? pickNextVisit(b, practitionerId) : null);
}

/** The ready-list home (the default Today on dev): the row it already picks as the next visit to be seen, when that row
 *  is the signed-in practitioner's own (live 4 Nov: Today on dev renders ReadyListHome, not the worklist page). */
export function readyNextTarget(item: ListAppointmentItem | undefined, practitionerId: string | null | undefined): WarmTarget | null {
  return item ? pickNextVisit({ inProgress: [item], upcoming: [] }, practitionerId) : null;
}

/** Live 4 Nov: the ready list's "next" is the next visit not yet started by the clock, so a patient already checked in and
 *  waiting (the one the clinician sees next) was passed over. A checked-in or arrived visit of this clinician, earliest
 *  first, is warmed before the next one by the clock. */
const WAITING = new Set(['checkedin', 'arrived']);
export function waitingOrNext(rows: ReadonlyArray<{ id: string; item: ListAppointmentItem; startMs: number | null }>, nextId: string | null | undefined, practitionerId: string | null | undefined): WarmTarget | null {
  const waiting = rows
    .filter((r) => WAITING.has(String(r.item.status?.code ?? '').toLowerCase()))
    .sort((a, b) => (a.startMs ?? Infinity) - (b.startMs ?? Infinity))
    .map((r) => readyNextTarget(r.item, practitionerId))
    .find((t): t is WarmTarget => t !== null);
  return waiting ?? readyNextTarget(rows.find((r) => r.id === nextId)?.item, practitionerId);
}

/** Warms one visit (once per session), behind the switch and the engine being on. */
export function useIntelWarmTarget(next: WarmTarget | null) {
  const appointmentId = next?.appointmentId ?? null;
  const patientId = next?.patientId ?? null;
  useEffect(() => {
    if (!appointmentId || !patientId || !env.intelligenceEngineEnabled || !isEncounterFlagOn('intelWarm')) return;
    const start = () => void warmNextVisit({ appointmentId, patientId }, { fetch: (i, init) => fetch(i, init), token: () => authStorage.getAccessToken(), encounterOf: boundedEncounterOf });
    return scheduleWarm(start);
  }, [appointmentId, patientId]);
}
